import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { cn } from "../../lib/utils";
import { Button, Input, Select, Spinner } from "../ui";
import { AdvisorCard } from "./AdvisorCard";
import { ActionList } from "./ActionList";
import { Sparkles, TrendingUp, TrendingDown, RotateCcw, Repeat, ChevronDown } from "lucide-react";

const PRESETS = ["this_week", "last_week", "this_month", "last_month", "custom"];

function AutomationPanel({ entities }) {
  const { t, toast } = useApp();
  const [open, setOpen] = useState(false);
  const [a, setA] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/advisory/automation").then(({ data }) => setA(data)).catch(() => setA({ frequency: "off", scope: "group", in_digest: true })); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/advisory/automation", {
        frequency: a.frequency, scope: a.scope, entity_id: a.scope === "entity" ? (a.entity_id || entities[0]?.id) : null, in_digest: a.in_digest,
      });
      setA(data); toast(t("auto_saved"));
    } finally { setSaving(false); }
  };

  if (!a) return null;
  return (
    <div className="bg-surface rounded-xl border" data-testid="adv-automation-panel">
      <button onClick={() => setOpen((v) => !v)} data-testid="adv-automation-toggle"
        className="w-full flex items-center gap-2 px-5 py-3 text-left">
        <Repeat className="w-4 h-4 text-primary" />
        <span className="text-sm font-medium">{t("auto_title")}</span>
        {a.frequency !== "off" && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary/12 text-primary">{t(`auto_${a.frequency}`)}</span>}
        <ChevronDown className={cn("w-4 h-4 text-text-secondary ml-auto transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-3 border-t pt-4">
          <p className="text-xs text-text-secondary">{t("auto_subtitle")}</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-text-secondary">{t("auto_freq")}</label>
              <Select value={a.frequency} onChange={(e) => setA({ ...a, frequency: e.target.value })} data-testid="adv-automation-freq" className="mt-1">
                <option value="off">{t("auto_off")}</option>
                <option value="weekly">{t("auto_weekly")}</option>
                <option value="monthly">{t("auto_monthly")}</option>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-text-secondary">{t("auto_scope")}</label>
              <Select value={a.scope} onChange={(e) => setA({ ...a, scope: e.target.value })} data-testid="adv-automation-scope" className="mt-1">
                <option value="group">{t("adv_scope_group")}</option>
                <option value="entity">{t("adv_scope_entity")}</option>
              </Select>
            </div>
          </div>
          {a.scope === "entity" && (
            <Select value={a.entity_id || ""} onChange={(e) => setA({ ...a, entity_id: e.target.value })} data-testid="adv-automation-entity">
              {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </Select>
          )}
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!a.in_digest} onChange={(e) => setA({ ...a, in_digest: e.target.checked })} data-testid="adv-automation-digest" />
            {t("auto_in_digest")}
          </label>
          <div className="flex items-center gap-3">
            <Button onClick={save} disabled={saving} data-testid="adv-automation-save">{t("auto_save")}</Button>
            <span className="text-[11px] text-amber-500">{t("auto_cost_note")}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function ScopeSelect({ scope, setScope, entityIds, setEntityIds, entities }) {
  const { t } = useApp();
  const toggle = (id) => setEntityIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider font-semibold text-text-secondary">{t("adv_scope")}</label>
      <div className="flex gap-2 mt-1.5 flex-wrap">
        {["group", "entity", "entities"].map((s) => (
          <button key={s} onClick={() => { setScope(s); setEntityIds([]); }} data-testid={`adv-scope-${s}`}
            className={cn("text-xs px-3 py-1.5 rounded-lg border transition-colors",
              scope === s ? "bg-text-primary text-background border-text-primary" : "hover:border-primary/40")}>
            {t(`adv_scope_${s}`)}
          </button>
        ))}
      </div>
      {(scope === "entity" || scope === "entities") && (
        <div className="flex gap-1.5 mt-2 flex-wrap">
          {entities.map((e) => {
            const on = entityIds.includes(e.id);
            return (
              <button key={e.id} data-testid={`adv-scope-ent-${e.key}`}
                onClick={() => scope === "entity" ? setEntityIds([e.id]) : toggle(e.id)}
                className={cn("text-xs px-2.5 py-1 rounded-full border transition-colors inline-flex items-center gap-1.5",
                  on ? "border-primary text-primary bg-primary/5" : "text-text-secondary hover:border-primary/40")}>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: e.color }} />{e.name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ReviewResult({ review, meta, reload }) {
  const { t, lang } = useApp();
  return (
    <div className="space-y-6" data-testid="adv-review-result">
      <div className="bg-surface rounded-xl border p-5">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-text-secondary">
          {review.range_label} · {new Date(review.created_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US")}
        </p>
        <h2 className="font-heading font-bold text-lg mt-1 flex items-center gap-2"><Sparkles className="w-4.5 h-4.5 text-primary" />{t("adv_summary")}</h2>
        <p className="text-sm text-text-primary mt-2 leading-relaxed">{review.overall_summary}</p>
        {(review.improvements?.length > 0 || review.regressions?.length > 0) && (
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            {review.improvements?.length > 0 && (
              <div data-testid="adv-improvements">
                <p className="text-xs font-semibold text-emerald-500 flex items-center gap-1.5 mb-1.5"><TrendingUp className="w-3.5 h-3.5" />{t("adv_improvements")}</p>
                <ul className="space-y-1">{review.improvements.map((x, i) => <li key={i} className="text-sm text-text-secondary leading-snug">• {x}</li>)}</ul>
              </div>
            )}
            {review.regressions?.length > 0 && (
              <div data-testid="adv-regressions">
                <p className="text-xs font-semibold text-red-500 flex items-center gap-1.5 mb-1.5"><TrendingDown className="w-3.5 h-3.5" />{t("adv_regressions")}</p>
                <ul className="space-y-1">{review.regressions.map((x, i) => <li key={i} className="text-sm text-text-secondary leading-snug">• {x}</li>)}</ul>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-3">
        {review.advisors.map((a) => <AdvisorCard key={a.key} advisor={a} />)}
      </div>

      <ActionList actions={review.actions} actionBase={`/advisory/reviews/${review.id}`} meta={meta} onChange={reload} />
    </div>
  );
}

export default function PerformanceReview({ meta, activeReview, setActiveReview, onSaved }) {
  const { t, lang, entities, toast } = useApp();
  const [preset, setPreset] = useState("this_month");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [scope, setScope] = useState("group");
  const [entityIds, setEntityIds] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(false);
  const pollRef = useRef(null);

  const reloadReview = useCallback(async () => {
    if (!activeReview?.id) return;
    const { data } = await api.get(`/advisory/reviews/${activeReview.id}`);
    setActiveReview(data);
  }, [activeReview?.id, setActiveReview]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const poll = (id) => {
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/advisory/reviews/${id}`);
        if (data.status !== "generating") {
          clearInterval(pollRef.current);
          setGenerating(false);
          if (data.status === "error") { setError(true); }
          else { setActiveReview(data); onSaved?.(); }
        }
      } catch {
        clearInterval(pollRef.current); setGenerating(false); setError(true);
      }
    }, 4000);
  };

  const generate = async () => {
    setError(false); setGenerating(true); setActiveReview(null);
    try {
      const body = { preset, scope, lang, entity_ids: entityIds };
      if (preset === "custom") { body.start = start; body.end = end; }
      const { data } = await api.post("/advisory/reviews", body);
      poll(data.id);
    } catch {
      setGenerating(false); setError(true); toast(t("adv_error"));
    }
  };

  return (
    <div className="space-y-6">
      <AutomationPanel entities={entities} />
      {/* Form */}
      <div className="bg-surface rounded-xl border p-5">
        <div className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-text-secondary">{t("adv_range")}</label>
            <div className="flex gap-2 mt-1.5 flex-wrap">
              {PRESETS.map((p) => (
                <button key={p} onClick={() => setPreset(p)} data-testid={`adv-preset-${p}`}
                  className={cn("text-xs px-3 py-1.5 rounded-lg border transition-colors",
                    preset === p ? "bg-text-primary text-background border-text-primary" : "hover:border-primary/40")}>
                  {t(`adv_${p}`)}
                </button>
              ))}
            </div>
            {preset === "custom" && (
              <div className="flex gap-3 mt-3 flex-wrap">
                <div><label className="text-xs text-text-secondary">{t("adv_from")}</label>
                  <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="adv-custom-start" className="mt-1" /></div>
                <div><label className="text-xs text-text-secondary">{t("adv_to")}</label>
                  <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="adv-custom-end" className="mt-1" /></div>
              </div>
            )}
          </div>
          <ScopeSelect scope={scope} setScope={setScope} entityIds={entityIds} setEntityIds={setEntityIds} entities={entities} />
          <div className="flex gap-2">
            <Button onClick={generate} disabled={generating || (scope !== "group" && entityIds.length === 0)} data-testid="adv-generate-btn">
              <Sparkles className="w-4 h-4 mr-1.5" />{t("adv_generate")}
            </Button>
            {activeReview && !generating && (
              <Button variant="ghost" onClick={() => setActiveReview(null)} data-testid="adv-new-review-btn">
                <RotateCcw className="w-4 h-4 mr-1.5" />{t("adv_generate")}
              </Button>
            )}
          </div>
        </div>
      </div>

      {generating && (
        <div className="bg-surface rounded-xl border p-10 flex flex-col items-center justify-center text-center" data-testid="adv-generating">
          <Spinner className="w-8 h-8 text-primary" />
          <p className="text-sm font-medium mt-4">{t("adv_generating")}</p>
          <p className="text-xs text-text-secondary mt-1">{t("adv_generating_hint")}</p>
        </div>
      )}
      {error && !generating && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-5 text-sm text-red-500" data-testid="adv-error">{t("adv_error")}</div>
      )}
      {activeReview && !generating && activeReview.status !== "generating" && (
        <ReviewResult review={activeReview} meta={meta} reload={reloadReview} />
      )}
    </div>
  );
}
